import { designVariables } from "@studio3t/design-variables";
import { Tabs, tabButtonId, tabPanelId } from "@studio3t/generic-components";
import { type ReactNode, useId, useState } from "react";
import styled from "styled-components";
import { Section } from "./styles";

const CARDS = designVariables.ui.cards;
const SEMANTIC = designVariables.ui.semantic;

type TabId = "pipelines" | "scripts";

// A service with both pipeline usage and script sources shows them under one
// tab bar (Figma 13984-223188). Like the pipeline detail's tabbed section,
// the table renders in the panel attached to the bar's bottom border, while
// the script cards sit detached below it. The panels are not focus stops of
// their own: from the bar, Tab goes straight to the first control inside the
// active panel (the table's sort headers, the script card's Copy), per the
// ARIA tabs pattern.
export function UsageTabs({
  pipelinesBody,
  scriptsBody,
}: {
  pipelinesBody: ReactNode;
  scriptsBody: ReactNode;
}) {
  const [active, setActive] = useState<TabId>("pipelines");
  const idBase = useId();

  return (
    <Section>
      <Bar
        tabs={[
          { id: "pipelines", label: "Used by pipelines" },
          { id: "scripts", label: "Scripts" },
        ]}
        activeId={active}
        onChange={(id) => setActive(id as TabId)}
        label="Service usage"
        idBase={idBase}
      />
      {active === "pipelines" ? (
        <Panel
          role="tabpanel"
          id={tabPanelId(idBase, active)}
          aria-labelledby={tabButtonId(idBase, active)}
        >
          {pipelinesBody}
        </Panel>
      ) : (
        <ScriptsPanel
          role="tabpanel"
          id={tabPanelId(idBase, active)}
          aria-labelledby={tabButtonId(idBase, active)}
        >
          {scriptsBody}
        </ScriptsPanel>
      )}
    </Section>
  );
}

// The tab's focus ring is drawn outside its box (see the library's tab
// tokens), so its bottom stroke lies in the panel just below the hairline —
// where the table's sticky header cells painted over it. The bar stacks
// above the panel so the ring stays whole on all four sides.
const Bar = styled(Tabs)`
  position: relative;
  z-index: 1;
`;

// Content card below the tab bar. Its top edge is the bar's shared bottom
// border, so it carries only side + bottom borders and rounds its bottom
// corners.
const Panel = styled.div`
  background: ${(props) => props.theme.cards1Background};
  border: 1px solid ${(props) => props.theme.cards1Border};
  border-top: none;
  border-bottom-left-radius: ${CARDS.corner}px;
  border-bottom-right-radius: ${CARDS.corner}px;
  overflow: hidden;
`;

// The Scripts tab's body: standalone script cards detached from the bar by
// the section gap, as on the pipeline detail page.
const ScriptsPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SEMANTIC.spacingl}px;
  padding-top: ${SEMANTIC.spacingl}px;
`;
